// "Generate next pack" (contracts/studio.md §5): a levelpack job for one track. By default the engine builds exactly
// what the CLI would (the track's schedule, its type mix and difficulty ramp, canonical seeds). "Customize" changes
// the draw weights (every registry type of the track is offered, so a new plugin appears here by itself), the
// difficulty line of the pack, or the seed (another take). Several consecutive packs can be queued at once.

import { useMemo, useState } from "react";
import { MAX_PACKS_PER_JOB, useCreateLevelPackJob, type GeneratorTrack, type NewLevelPackJob } from "../../api/levelPacks";
import { useAuth, useCan } from "../../auth/AuthContext";
import { Button, CategoryTag, Chip, Field, Select, TextInput } from "../../components/ui";
import { typeSlug } from "../../lib/format";
import { StudioError } from "../studio/components";
import s from "../studio/studio.module.css";
import l from "./levelpacks.module.css";

const num = (v: string) => (v.trim() === "" ? NaN : Number(v));
const SEED = /^[A-Za-z0-9._:-]{1,64}$/;
const round2 = (x: number) => Math.round(x * 100) / 100;

export function LevelPackForm({
  tracks,
  initialTrack,
  lockTrack = false,
  onQueued,
  onCancel,
}: {
  tracks: GeneratorTrack[];
  initialTrack?: string | null;
  lockTrack?: boolean;
  onQueued: (jobId: string) => void;
  onCancel?: () => void;
}) {
  const canEdit = useCan("studio.edit");
  const { admin } = useAuth();
  const create = useCreateLevelPackJob();
  const [trackId, setTrackId] = useState(() => (initialTrack && tracks.some((t) => t.id === initialTrack) ? initialTrack : (tracks[0]?.id ?? "")));
  const track = tracks.find((t) => t.id === trackId) ?? null;
  const [count, setCount] = useState("1");
  const [custom, setCustom] = useState(false);
  const [mix, setMix] = useState<Record<string, string>>({});
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [seed, setSeed] = useState("");

  const defaults = useMemo(() => {
    if (!track) return { mix: {} as Record<string, string>, start: "", end: "" };
    const m: Record<string, string> = {};
    for (const t of track.availableTypes) m[t.type] = String(round2(track.next.typeMix[t.type] ?? 0));
    return { mix: m, start: String(round2(track.next.difficulty.start)), end: String(round2(track.next.difficulty.end)) };
  }, [track]);
  const pickTrack = (id: string) => {
    setTrackId(id);
    setMix({});
    setStart("");
    setEnd("");
    create.reset();
  };
  const weightOf = (type: string) => mix[type] ?? defaults.mix[type] ?? "0";
  const startV = start || defaults.start;
  const endV = end || defaults.end;

  const n = num(count);
  const countError = !Number.isInteger(n) || n < 1 ? "A whole number, 1 or more." : n > MAX_PACKS_PER_JOB ? `At most ${MAX_PACKS_PER_JOB} packs per job.` : null;
  const weights = track ? track.availableTypes.map((t) => [t.type, num(weightOf(t.type))] as const) : [];
  const total = weights.reduce((a, [, w]) => a + (w > 0 ? w : 0), 0);
  const mixError = !custom ? null : weights.some(([, w]) => !(w >= 0)) ? "Weights are 0 or more." : total <= 0 ? "Give at least one type a weight above 0." : null;
  const lo = num(startV);
  const hi = num(endV);
  const curveError = !custom ? null : !(lo >= 0 && lo <= 1 && hi >= 0 && hi <= 1) ? "Targets run from 0 to 1." : null;
  const seedError = custom && seed && !SEED.test(seed) ? "Letters, digits and . _ : - only (64 at most)." : null;

  const mixChanged = custom && track ? weights.some(([t, w]) => round2(w) !== round2(track.next.typeMix[t] ?? 0)) : false;
  const curveChanged = custom && track ? round2(lo) !== round2(track.next.difficulty.start) || round2(hi) !== round2(track.next.difficulty.end) : false;
  const isCustom = mixChanged || curveChanged || Boolean(custom && seed);
  const invalid = Boolean(!track || countError || mixError || curveError || seedError);

  const submit = () => {
    if (!track || invalid) return;
    const body: NewLevelPackJob = { kind: "levelpack", track: track.id, count: n };
    if (mixChanged) body.typeMix = Object.fromEntries(weights.filter(([, w]) => w > 0).map(([t, w]) => [t, w]));
    if (curveChanged) body.difficulty = { start: lo, end: hi };
    if (custom && seed) body.seed = seed;
    create.mutate(body, { onSuccess: ({ job }) => onQueued(job.id) });
  };

  if (!track) return <p className={l.faint}>The engine lists no tracks.</p>;
  const next = track.next;
  const lastIndex = next.index + (Number.isFinite(n) && n > 0 ? n - 1 : 0);
  const newTypes = track.availableTypes.filter((t) => !track.types.includes(t.type));
  const introHere = Object.entries(next.intro);

  return (
    <form
      className={s.form}
      data-testid="levelpack-form"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      {lockTrack ? null : (
        <Field label="Track">
          <Select value={trackId} onChange={(e) => pickTrack(e.target.value)} aria-label="Track">
            {tracks.map((t) => (
              <option key={t.id} value={t.id}>
                {t.title} · next pack {t.nextIndex}
              </option>
            ))}
          </Select>
        </Field>
      )}

      <div className={l.nextPack} data-testid="next-pack">
        <div className={l.nextHead}>
          <CategoryTag category={track.id} />
          <strong>
            Pack {next.index}
            {lastIndex > next.index ? `–${lastIndex}` : ""}
          </strong>
          <span className="mono">
            L{next.first}–L{next.first + (lastIndex - next.index + 1) * 50 - 1}
          </span>
          <Chip tone="violet">{next.chapter.name}</Chip>
          <span className="mono">{next.chapter.palette}</span>
        </div>
        <div className={l.nextMeta}>
          <span>
            <b>{track.published}</b> published
          </span>
          <span>
            <b>{track.drafts.length}</b> {track.drafts.length === 1 ? "draft" : "drafts"} in review
          </span>
          <span>
            schedule <b className="mono">{next.generator}</b>
          </span>
          <span>
            target <b className="mono">{next.difficulty.start.toFixed(2)} → {next.difficulty.end.toFixed(2)}</b>
          </span>
        </div>
        <div className={l.mixChips} aria-label="Type mix of the schedule">
          {Object.entries(next.typeMix).map(([t, w]) => (
            <span key={t} className={l.mixChip}>
              {typeSlug(t)} <span className="mono">{Math.round((w / Math.max(1e-9, Object.values(next.typeMix).reduce((a, b) => a + b, 0))) * 100)}%</span>
            </span>
          ))}
          {introHere.map(([at, t]) => (
            <Chip key={at} tone="info" title="Introduced in this pack by the schedule">
              intro {typeSlug(t)} · L{at}
            </Chip>
          ))}
        </div>
      </div>

      <div className={s.row}>
        <Field label="Packs" hint={countError ? <span className={s.locked}>{countError}</span> : "Consecutive packs of 50 levels, each a draft to review."}>
          <TextInput type="number" min={1} max={MAX_PACKS_PER_JOB} value={count} onChange={(e) => setCount(e.target.value)} aria-label="Packs" aria-invalid={Boolean(countError)} />
        </Field>
        <div className={s.fieldCol}>
          <span className="monoLabel">Build</span>
          <div className={s.kinds} role="radiogroup" aria-label="Build">
            <button type="button" role="radio" className={s.kind} aria-checked={!custom} onClick={() => setCustom(false)}>
              <strong>Schedule</strong>
              Same as the CLI
            </button>
            <button type="button" role="radio" className={s.kind} aria-checked={custom} onClick={() => setCustom(true)}>
              <strong>Customize</strong>
              Mix, curve, seed
            </button>
          </div>
        </div>
      </div>

      {custom ? (
        <>
          <div className={s.fieldCol}>
            <span className="monoLabel">Type mix · draw weights</span>
            <div className={l.mixTable} role="table" aria-label="Type mix" data-testid="type-mix">
              {track.availableTypes.map((t) => {
                const w = num(weightOf(t.type));
                const share = total > 0 && w > 0 ? w / total : 0;
                return (
                  <div key={t.type} className={l.mixRow} role="row" data-type={t.type}>
                    <span className={l.mixName} role="cell">
                      <strong>{typeSlug(t.type)}</strong>
                      <span className="mono">{t.type}</span>
                    </span>
                    <span role="cell">
                      {t.inSchedule ? (
                        <Chip tone="muted">schedule</Chip>
                      ) : track.types.includes(t.type) ? (
                        <Chip tone="muted" title="In another era of this track's schedule (an earlier or a later pack)">
                          other era
                        </Chip>
                      ) : (
                        <Chip tone="info" title="In the engine's registry but not in this track's schedule yet: it gets an intro level the first time">
                          new
                        </Chip>
                      )}
                    </span>
                    <TextInput
                      type="number"
                      min={0}
                      step={0.05}
                      value={weightOf(t.type)}
                      onChange={(e) => setMix((m) => ({ ...m, [t.type]: e.target.value }))}
                      aria-label={`Weight of ${t.type}`}
                      className={l.weightInput}
                    />
                    <span className={l.shareBar} role="cell" aria-label={`${Math.round(share * 100)}%`}>
                      <span style={{ width: `${share * 100}%` }} />
                    </span>
                    <span className="mono" role="cell">
                      {Math.round(share * 100)}%
                    </span>
                  </div>
                );
              })}
            </div>
            {mixError ? <span className={s.locked}>{mixError}</span> : null}
            <span className={l.faint}>
              A type the track has never shown is introduced on its own level (how-to, easier), never on a relief or a gate.
              {newTypes.length ? ` ${newTypes.length} registry type${newTypes.length === 1 ? "" : "s"} not in the schedule.` : ""}
            </span>
          </div>
          <div className={s.row}>
            <Field label="Target, first level" hint="0–1; gates and reliefs keep their saw-tooth">
              <TextInput type="number" min={0} max={1} step={0.01} value={startV} onChange={(e) => setStart(e.target.value)} aria-label="Target of the first level" />
            </Field>
            <Field label="Target, 50th level">
              <TextInput type="number" min={0} max={1} step={0.01} value={endV} onChange={(e) => setEnd(e.target.value)} aria-label="Target of the last level" />
            </Field>
          </div>
          {curveError ? <span className={s.locked}>{curveError}</span> : null}
          <Field label="Seed (optional)" hint={seedError ? <span className={s.locked}>{seedError}</span> : "Another take on the same levels; empty keeps the canonical seeds."}>
            <TextInput value={seed} onChange={(e) => setSeed(e.target.value)} placeholder="take-2" aria-label="Seed" maxLength={64} />
          </Field>
        </>
      ) : null}

      <StudioError error={create.error} />

      <div className={s.submitRow}>
        {canEdit ? (
          <Button type="submit" variant="primary" busy={create.isPending} disabled={invalid}>
            {Number.isFinite(n) && n > 1 ? `Generate ${n} packs` : "Generate next pack"}
          </Button>
        ) : (
          <Chip tone="muted">generating packs needs the editor role (you are {admin?.role})</Chip>
        )}
        {onCancel ? (
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
        <span className={s.summary}>
          {isCustom ? "Custom" : "Schedule"} · {track.title} · 50 levels per pack · solver-verified · drafts until published
        </span>
      </div>
    </form>
  );
}
